import { createForm } from "@tanstack/solid-form";
import { onMount, Show } from "solid-js";
import { produce } from "solid-js/store";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { CurrencyInput } from "@/components/shared/input";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  FieldSectionTitle,
} from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch, SwitchControl, SwitchThumb } from "@/components/ui/switch";
import { EFFORT, REMOTE_POLICY } from "@/domains/job-offer/create/constants";
import {
  createCurrencyValidatorWithMinSchema,
  MIN_AMOUNT_EUR,
} from "@/libs/validators/currency";
import { useOnboardingStore } from "../../context/OnboardingContext";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";

const {
  validationFunction: hourlyRateValidationFunction,
  schema: hourlyRateSchema,
} = createCurrencyValidatorWithMinSchema(MIN_AMOUNT_EUR);

const schemaValidation = z.object({
  price_per_day: hourlyRateSchema,
  is_available: z.boolean().default(true),
  work_preference: z.enum(REMOTE_POLICY).default(REMOTE_POLICY[0]),
  weekly_hours: z.enum(EFFORT),
});

export const Pricing = () => {
  const [store, setStore] = useOnboardingStore();

  const form = createForm(() => ({
    defaultValues: {
      price_per_day: store.pricing.value.price_per_day,
      is_available: store.info.value.is_available ?? true,
      weekly_hours:
        store.info.value.weekly_hours ?? ("40" as (typeof EFFORT)[number]),
      work_preference: store.info.value.work_preference ?? REMOTE_POLICY[0],
    },
  }));

  const { t } = useI18n();

  onMount(() => {
    if (!store.pricing.value.price_per_day) {
      setStore(
        produce((draft) => {
          draft.pricing.error = "La tariffa giornaliera è obbligatoria";
        }),
      );
    }
  });

  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>{t("onboarding.steps.pricing.title")}</OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.pricing.description")}
        </OnboardingDescription>
      </div>
      <div class="border-b p-4 rounded-lg bg-card border-neutral-200 pb-4 w-full">
        <FieldSectionTitle class="text-md font-medium">
          {t("availability.title")}
        </FieldSectionTitle>
        <p class="text-sm text-gray-500">
          Indica se sei disponibile per lavorare.
        </p>
        <div class="flex items-center gap-4">
          <form.Field
            name="is_available"
            validators={{
              onChange: z.boolean(),
            }}
          >
            {(field) => (
              <div class="flex items-center gap-3 w-full">
                <Switch
                  checked={field().state.value}
                  onChange={(v) => {
                    field().setValue(v);
                    setStore(
                      produce((draft) => {
                        draft.info.value.is_available = v;
                        draft.info.error = null;
                      }),
                    );
                  }}
                >
                  <SwitchControl>
                    <SwitchThumb />
                  </SwitchControl>
                </Switch>
                <span class="text-sm">{t("availability.available")}</span>
              </div>
            )}
          </form.Field>
          <form.Subscribe
            selector={(state) => ({
              is_available: state.values.is_available,
            })}
          >
            {(state) => (
              <div class="flex items-center gap-4 w-full md:w-2/3">
                <form.Field
                  name="weekly_hours"
                  validators={{ onChange: z.enum(EFFORT) }}
                >
                  {(field) => (
                    <FieldContainer>
                      <FieldContainerLabel for={field().name}>
                        {t("availability.weekly_hours")}
                      </FieldContainerLabel>
                      <Select
                        disabled={!state().is_available}
                        class="bg-white"
                        value={field().state.value}
                        onChange={(value) => {
                          const nextValue = value ?? field().state.value;
                          field().handleChange(nextValue);
                          setStore(
                            produce((draft) => {
                              draft.info.value.weekly_hours = nextValue;
                            }),
                          );
                        }}
                        options={[...EFFORT]}
                        itemComponent={(item) => (
                          <SelectItem {...item}>
                            {item.item.rawValue}{" "}
                            {t("availability.h_weekly_hours")}
                          </SelectItem>
                        )}
                      >
                        <SelectTrigger>
                          <SelectValue>
                            <Show
                              when={field().state.value}
                              fallback={t("availability.select_hours")}
                            >
                              {field().state.value}{" "}
                              {t("availability.h_weekly_hours")}
                            </Show>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent />
                      </Select>
                      <FieldInfo field={field()} />
                    </FieldContainer>
                  )}
                </form.Field>
                <form.Field
                  name="work_preference"
                  validators={{ onChange: z.enum(REMOTE_POLICY) }}
                >
                  {(field) => (
                    <FieldContainer>
                      <FieldContainerLabel for={field().name}>
                        {t("profile.edit.dialog.workPreference")}
                      </FieldContainerLabel>
                      <Select
                        class="bg-white"
                        value={field().state.value}
                        onChange={(value) => {
                          const nextValue = value ?? field().state.value;
                          field().handleChange(nextValue);
                          setStore(
                            produce((draft) => {
                              draft.info.value.work_preference = nextValue;
                            }),
                          );
                        }}
                        options={[...REMOTE_POLICY]}
                        itemComponent={(item) => (
                          <SelectItem {...item}>
                            {t(
                              `common.remote_policy_options.${item.item.rawValue as (typeof REMOTE_POLICY)[number]}`,
                            )}
                          </SelectItem>
                        )}
                      >
                        <SelectTrigger>
                          <SelectValue>
                            <Show
                              when={field().state.value}
                              fallback={t(
                                "profile.edit.dialog.workPreferencePlaceholder",
                              )}
                            >
                              {t(
                                `common.remote_policy_options.${field().state.value as (typeof REMOTE_POLICY)[number]}`,
                              )}
                            </Show>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent />
                      </Select>
                      <FieldInfo field={field()} />
                    </FieldContainer>
                  )}
                </form.Field>
              </div>
            )}
          </form.Subscribe>
        </div>
      </div>
      <div class="flex items-end gap-4 border-b border-gray-200 pb-8">
        <div class="w-full">
          <h2 class="text-lg md:text-2xl font-medium">
            {t("onboarding.steps.pricing.dailyRate")}
          </h2>
          <p class="hidden md:block text-sm md:text-lg text-gray-500">
            {t("onboarding.steps.pricing.dailyRateDescription")}
          </p>
        </div>
        <div class="flex flex-col gap-2 w-full md:w-2/3">
          <form.Field
            name="price_per_day"
            validators={{
              onChange: schemaValidation.shape.price_per_day,
              onBlur: ({ value }) => {
                const result = hourlyRateValidationFunction(value);
                if (!result.isValid) {
                  setStore(
                    produce((draft) => {
                      draft.pricing.error = result.error ?? null;
                    }),
                  );
                  return result.error;
                }
                setStore(
                  produce((draft) => {
                    draft.pricing.value.price_per_day = result.value!;
                    draft.pricing.error = null;
                  }),
                );
              },
            }}
          >
            {(field) => (
              <FieldContainer>
                <FieldContainerLabel for={field().name}>
                  {t("onboarding.steps.pricing.dailyRate")}
                </FieldContainerLabel>
                <CurrencyInput
                  class="w-full"
                  value={field().state.value ?? ""}
                  fieldApi={field()}
                  placeholder="Inserisci tariffa giornaliera"
                />
                <FieldInfo field={field()} />
              </FieldContainer>
            )}
          </form.Field>
        </div>
      </div>
    </div>
  );
};
